import JSZip from "jszip";

const formatInr = (n) =>
  Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

/** Empty / missing salary — never invent company components or amounts. */
export const EMPTY_SALARY_ROWS = [];

/** @deprecated Kept so older imports do not crash; always empty. */
export const SAMPLE_SALARY_STRUCTURE_ROWS = EMPTY_SALARY_ROWS;

export function mapComponentsToSalaryRows(components = [], amountsByCode = {}) {
  if (!Array.isArray(components) || !components.length) return [];
  return components
    .filter((c) => c && c.isActive !== false)
    .map((c) => {
      const code = String(c.code || "").toUpperCase();
      const category = String(c.category || "EARNING").toUpperCase();
      const type =
        category.includes("EMPLOYER") || category === "BENEFIT"
          ? "employer"
          : category === "DEDUCTION"
            ? "deduction"
            : "earning";
      const monthlyRaw =
        amountsByCode[code] ??
        amountsByCode[c.code] ??
        c.monthly ??
        c.monthlyAmount ??
        null;
      const monthly = monthlyRaw == null || monthlyRaw === "" ? null : Number(monthlyRaw);
      const annual =
        monthly == null || Number.isNaN(monthly) ? null : Math.round(monthly * 12);
      return {
        code,
        name: c.name || code,
        monthly: Number.isFinite(monthly) ? monthly : null,
        annual: Number.isFinite(annual) ? annual : null,
        type,
      };
    });
}

/**
 * Build rows from employee active structure (`resolvedComponentsJson`).
 * Only includes components that exist on the structure — no invented heads.
 * Supports simulator shapes: `{ name, code, amount, annualAmount, category }`.
 */
export function mapResolvedStructureToSalaryRows(structure) {
  if (!structure) return [];
  let resolved =
    structure.resolvedComponentsJson ||
    structure.resolved_components_json ||
    structure.components ||
    [];
  if (typeof resolved === "string") {
    try {
      resolved = JSON.parse(resolved);
    } catch {
      resolved = [];
    }
  }
  if (!Array.isArray(resolved) || !resolved.length) return [];

  return resolved
    .map((c) => {
      if (c == null) return null;
      const code = String(c.code || c.componentCode || "")
        .toUpperCase()
        .trim();
      const name = c.name || c.componentName || code;
      const monthly = Number(
        c.monthly ??
          c.monthlyAmount ??
          c.amountMonthly ??
          c.amount ??
          c.value ??
          0
      );
      const annual = Number(
        c.annual ??
          c.annualAmount ??
          c.amountAnnual ??
          (Number.isFinite(monthly) ? monthly * 12 : 0)
      );
      const category = String(c.category || c.type || c.section || "EARNING").toUpperCase();
      let type = "earning";
      if (category.includes("EMPLOYER") || category === "BENEFIT" || /employer/i.test(String(c.type || ""))) {
        type = "employer";
      } else if (category.includes("DEDUCT") || /deduct/i.test(String(c.type || ""))) {
        type = "deduction";
      } else if (category.includes("REIMBURSE")) {
        type = "reimbursement";
      }
      // Name heuristics when category missing (saved earnings often have only {name, amount})
      if (!c.category && !c.type) {
        const n = String(name).toLowerCase();
        if (/\b(employer|er pf|edli|admin charge|gratuity)\b/.test(n) && !/\bemployee\b/.test(n)) {
          type = "employer";
        } else if (/\b(pf \(employee\)|esi \(employee\)|professional tax|tds|deduction)\b/.test(n)) {
          type = "deduction";
        } else if (/\breimburs/.test(n)) {
          type = "reimbursement";
        }
      }
      if (!code && !name) return null;
      return {
        code: code || String(name).toUpperCase().replace(/\s+/g, "_"),
        name,
        monthly: Number.isFinite(monthly) ? monthly : 0,
        annual: Number.isFinite(annual) ? annual : 0,
        type,
      };
    })
    .filter(Boolean);
}

/** Unwrap / JSON-parse API or Sequelize structure payload. */
export function normalizeEmployeeStructureRecord(raw) {
  if (!raw || typeof raw !== "object") return null;
  let s = raw;
  if (s.success && s.data != null && typeof s.data === "object") s = s.data;
  else if (s.data != null && typeof s.data === "object" && (s.data.id || s.data.resolvedComponentsJson || s.data.targetAnnualCtc)) {
    s = s.data;
  }
  if (!s || typeof s !== "object") return null;

  const parseJson = (v, fallback) => {
    if (v == null) return fallback;
    if (typeof v === "string") {
      try {
        return JSON.parse(v);
      } catch {
        return fallback;
      }
    }
    return v;
  };

  const resolved = parseJson(s.resolvedComponentsJson ?? s.resolved_components_json, []);
  const summary = parseJson(s.computedSummaryJson ?? s.computed_summary_json, null);
  const target = s.targetAnnualCtc ?? s.target_annual_ctc;
  const hasSignal =
    s.id != null ||
    (Array.isArray(resolved) && resolved.length > 0) ||
    target != null ||
    (summary && (summary.annualAmount != null || summary.monthlyCtc != null || summary.annualCtc != null));
  if (!hasSignal) return null;

  return {
    ...s,
    resolvedComponentsJson: Array.isArray(resolved) ? resolved : [],
    computedSummaryJson: summary,
    targetAnnualCtc: target,
  };
}

/**
 * Load best available salary structure for letter tags.
 * Tries active → latest listed → never invents components.
 */
export async function fetchEmployeeSalaryForLetter(employeeId, {
  getActiveStructure,
  listEmployeeStructures,
} = {}) {
  const empKey = employeeId;
  if (!empKey) return { rows: [], structure: null, warning: "No employee id" };

  let structure = null;
  let warning = "";

  try {
    if (typeof getActiveStructure === "function") {
      const res = await getActiveStructure(empKey);
      structure = normalizeEmployeeStructureRecord(res?.data !== undefined ? { success: true, data: res.data } : res);
      if (!structure && res?.data) structure = normalizeEmployeeStructureRecord(res.data);
      if (!structure) structure = normalizeEmployeeStructureRecord(res);
    }
  } catch (e) {
    warning = e?.message || "Active structure request failed";
  }

  if (!structure && typeof listEmployeeStructures === "function") {
    try {
      const res = await listEmployeeStructures(empKey);
      const list = res?.data || res?.structures || res || [];
      const arr = Array.isArray(list) ? list : [];
      const active = arr.find((x) => String(x.status).toLowerCase() === "active");
      const pick = active || arr[0];
      structure = normalizeEmployeeStructureRecord(pick);
      if (structure && !active) warning = "Using latest non-active salary structure";
    } catch (e) {
      warning = warning || e?.message || "List structures failed";
    }
  }

  if (!structure) {
    return {
      rows: [],
      structure: null,
      warning: warning || "No salary structure found for this employee",
    };
  }

  let rows = mapResolvedStructureToSalaryRows(structure);
  // If components empty but CTC exists, still expose CTC via summary helper
  return { rows, structure, warning };
}

function ensureAnnexureFromCtc(salaryMap) {
  const next = { ...salaryMap };
  const textMissing =
    !next.salary_structure_text ||
    String(next.salary_structure_text).includes("not available");
  if (!textMissing || !next.total_ctc) return next;
  const lines = [
    "Salary Structure (Annexure A)",
    "----------------------------------------------",
  ];
  if (next.gross_salary) lines.push(`Gross (Monthly)         ₹ ${next.gross_salary}`);
  lines.push(`Total CTC (Monthly)     ₹ ${next.total_ctc_monthly || "—"}`);
  lines.push(`Total CTC (Annual)      ₹ ${next.total_ctc}`);
  lines.push("");
  lines.push(
    "Note: Component-wise breakup will appear here after an active salary structure is assigned to the employee."
  );
  next.salary_structure_text = lines.join("\n");
  next.salary_structure_table = next.salary_structure_text;
  return next;
}

/** Prefer structure summary CTC when available. */
export function applyStructureSummaryToSalaryMap(salaryMap, structure) {
  if (!structure || !salaryMap) return salaryMap;
  const summary =
    typeof structure.computedSummaryJson === "string"
      ? (() => {
          try {
            return JSON.parse(structure.computedSummaryJson);
          } catch {
            return {};
          }
        })()
      : structure.computedSummaryJson || structure.computed_summary_json || {};
  const totals = summary.totals || summary || {};
  const annual =
    structure.targetAnnualCtc ??
    structure.target_annual_ctc ??
    summary.annualAmount ??
    summary.annualCtc ??
    totals.annualCtc ??
    totals.annualAmount;
  const monthly =
    summary.monthlyCtc ??
    totals.monthlyCtc ??
    totals.actualCtcMonthly ??
    (annual != null && annual !== "" && !Number.isNaN(Number(annual))
      ? Number(annual) / 12
      : null);
  const grossFromSummary = totals.earnings ?? totals.gross ?? summary.gross ?? summary.earnings;
  let next = { ...salaryMap };
  if (annual != null && annual !== "" && !Number.isNaN(Number(annual))) {
    next.total_ctc = formatInr(Math.round(Number(annual)));
  }
  if (monthly != null && monthly !== "" && !Number.isNaN(Number(monthly))) {
    next.total_ctc_monthly = formatInr(Math.round(Number(monthly)));
  }
  if ((!next.gross_salary || next.gross_salary === "") && grossFromSummary != null) {
    next.gross_salary = formatInr(Math.round(Number(grossFromSummary)));
  }
  return ensureAnnexureFromCtc(next);
}

/**
 * When no employee_salary_structures row exists, fall back to employees.ctc
 * so appointment letters still show Annual CTC (and a monthly estimate).
 */
export function applyEmployeeProfileCtcFallback(salaryMap, employee = {}) {
  if (!salaryMap) return salaryMap;
  if (salaryMap.total_ctc) return ensureAnnexureFromCtc(salaryMap);
  const raw =
    employee.ctc ??
    employee.annualCtc ??
    employee.annual_ctc ??
    employee.targetAnnualCtc ??
    employee.total_ctc;
  if (raw == null || raw === "" || Number.isNaN(Number(String(raw).replace(/,/g, "")))) {
    return salaryMap;
  }
  const annual = Math.round(Number(String(raw).replace(/,/g, "")));
  const monthly = Math.round(annual / 12);
  let next = {
    ...salaryMap,
    total_ctc: formatInr(annual),
    total_ctc_monthly: salaryMap.total_ctc_monthly || formatInr(monthly),
  };
  if (!next.gross_salary) {
    next.gross_salary = formatInr(monthly);
  }
  return ensureAnnexureFromCtc(next);
}

function formatDateForLetter(value) {
  if (!value) return "";
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(value);
  }
}


/** Keep only components with a non-zero monthly or annual amount (applicable for letters). */
export function filterApplicableSalaryRows(rows = []) {
  return (rows || []).filter((r) => {
    const monthly = Number(r?.monthly);
    const annual = Number(r?.annual);
    const hasMonthly = Number.isFinite(monthly) && monthly !== 0;
    const hasAnnual = Number.isFinite(annual) && annual !== 0;
    return hasMonthly || hasAnnual;
  });
}

function partitionSalaryRows(rows = []) {
  const earnings = rows.filter((r) => r.type === "earning");
  const benefits = rows.filter((r) => r.type === "employer");
  const reimbursements = rows.filter((r) => r.type === "reimbursement");
  const deductions = rows.filter((r) => r.type === "deduction");
  const sumMo = (list) => list.reduce((s, r) => s + Number(r.monthly || 0), 0);
  const sumAn = (list) => list.reduce((s, r) => s + Number(r.annual || 0), 0);
  const grossMonthly = sumMo(earnings);
  const grossAnnual = sumAn(earnings);
  const benefitsMonthly = sumMo(benefits);
  const benefitsAnnual = sumAn(benefits);
  const reimbMonthly = sumMo(reimbursements);
  const reimbAnnual = sumAn(reimbursements);
  const dedMonthly = sumMo(deductions);
  const dedAnnual = sumAn(deductions);
  return {
    earnings,
    benefits,
    reimbursements,
    deductions,
    grossMonthly,
    grossAnnual,
    benefitsMonthly,
    benefitsAnnual,
    reimbMonthly,
    reimbAnnual,
    dedMonthly,
    dedAnnual,
    netMonthly: Math.max(0, grossMonthly - dedMonthly),
    netAnnual: Math.max(0, grossAnnual - dedAnnual),
    ctcMonthly: grossMonthly + benefitsMonthly + reimbMonthly,
    ctcAnnual: grossAnnual + benefitsAnnual + reimbAnnual,
  };
}

export function buildSalaryStructurePlainText(rows = EMPTY_SALARY_ROWS) {
  const applicable = filterApplicableSalaryRows(rows);
  if (!applicable.length) {
    return "[Salary structure not available — configure components / employee structure]";
  }
  const sections = partitionSalaryRows(applicable);
  const lines = ["Salary Structure (Annexure A)", "Component                     Monthly          Annual"];
  const pushSection = (title, list, totalLabel, totalMo, totalAn) => {
    if (!list.length) return;
    lines.push("--------------------------------------------------------------");
    lines.push(title);
    list.forEach((r) => {
      const mo = r.monthly == null ? "—" : formatInr(r.monthly);
      const an = r.annual == null ? "—" : formatInr(r.annual);
      lines.push(`${String(r.name).padEnd(28)} ${String(mo).padStart(12)} ${String(an).padStart(14)}`);
    });
    lines.push(
      `${String(totalLabel).padEnd(28)} ${formatInr(totalMo).padStart(12)} ${formatInr(totalAn).padStart(14)}`
    );
  };
  pushSection("Earnings", sections.earnings, "Gross (Earnings)", sections.grossMonthly, sections.grossAnnual);
  pushSection("Benefits", sections.benefits, "Total Benefits", sections.benefitsMonthly, sections.benefitsAnnual);
  pushSection(
    "Reimbursements",
    sections.reimbursements,
    "Total Reimbursements",
    sections.reimbMonthly,
    sections.reimbAnnual
  );
  pushSection("Deductions", sections.deductions, "Total Deductions", sections.dedMonthly, sections.dedAnnual);
  lines.push("--------------------------------------------------------------");
  lines.push(
    `${"Net Pay".padEnd(28)} ${formatInr(sections.netMonthly).padStart(12)} ${formatInr(sections.netAnnual).padStart(14)}`
  );
  lines.push(
    `${"Total CTC".padEnd(28)} ${formatInr(sections.ctcMonthly).padStart(12)} ${formatInr(sections.ctcAnnual).padStart(14)}`
  );
  return lines.join("\n");
}

/**
 * OOXML Word table — sectioned annexure. Inherits template font; no ₹; skips zero rows.
 */
export function buildSalaryStructureWordTableXml(rows = EMPTY_SALARY_ROWS) {
  const applicable = filterApplicableSalaryRows(rows);
  const border = (name) =>
    `<w:${name} w:val="single" w:sz="4" w:space="0" w:color="666666"/>`;
  const borders = `<w:tblBorders>${border("top")}${border("left")}${border("bottom")}${border("right")}${border("insideH")}${border("insideV")}</w:tblBorders>`;

  const cell = (text, { bold = false, align = "left", shade = "" } = {}) => {
    const jc = align === "right" ? "<w:jc w:val=\"right\"/>" : "";
    const shd = shade
      ? `<w:shd w:val="clear" w:color="auto" w:fill="${shade}"/>`
      : "";
    const rPr = bold ? "<w:rPr><w:b/></w:rPr>" : "";
    return `<w:tc>
      <w:tcPr><w:tcW w:w="0" w:type="auto"/>${shd}</w:tcPr>
      <w:p><w:pPr>${jc}</w:pPr><w:r>${rPr}<w:t xml:space="preserve">${escapeXml(String(text ?? ""))}</w:t></w:r></w:p>
    </w:tc>`;
  };

  const rowXml = (c1, c2, c3, opts = {}) =>
    `<w:tr>${cell(c1, { bold: opts.bold, shade: opts.shade })}${cell(c2, {
      bold: opts.bold,
      align: "right",
      shade: opts.shade,
    })}${cell(c3, { bold: opts.bold, align: "right", shade: opts.shade })}</w:tr>`;

  const sectionHeader = (title) => rowXml(title, "", "", { bold: true, shade: "E5E7EB" });
  const fmt = (v) => (v == null || v === "" ? "—" : formatInr(v));

  if (!applicable.length) {
    return `<w:tbl>
      <w:tblPr><w:tblW w:w="5000" w:type="pct"/>${borders}<w:tblLayout w:type="autofit"/></w:tblPr>
      <w:tblGrid><w:gridCol w:w="3500"/><w:gridCol w:w="2000"/><w:gridCol w:w="2000"/></w:tblGrid>
      ${rowXml("Component", "Monthly", "Annual", { bold: true, shade: "F3F4F6" })}
      ${rowXml("Salary structure not available", "—", "—")}
    </w:tbl>`;
  }

  const s = partitionSalaryRows(applicable);
  const parts = [rowXml("Component", "Monthly", "Annual", { bold: true, shade: "F3F4F6" })];
  const appendSection = (title, list, totalLabel, totalMo, totalAn) => {
    if (!list.length) return;
    parts.push(sectionHeader(title));
    list.forEach((r) => parts.push(rowXml(r.name, fmt(r.monthly), fmt(r.annual))));
    parts.push(rowXml(totalLabel, fmt(totalMo), fmt(totalAn), { bold: true, shade: "F9FAFB" }));
  };
  appendSection("Earnings", s.earnings, "Gross (Earnings)", s.grossMonthly, s.grossAnnual);
  appendSection("Benefits", s.benefits, "Total Benefits", s.benefitsMonthly, s.benefitsAnnual);
  appendSection("Reimbursements", s.reimbursements, "Total Reimbursements", s.reimbMonthly, s.reimbAnnual);
  appendSection("Deductions", s.deductions, "Total Deductions", s.dedMonthly, s.dedAnnual);
  parts.push(rowXml("Net Pay", fmt(s.netMonthly), fmt(s.netAnnual), { bold: true, shade: "ECFDF5" }));
  parts.push(rowXml("Total CTC", fmt(s.ctcMonthly), fmt(s.ctcAnnual), { bold: true, shade: "EEF2FF" }));

  return `<w:tbl>
    <w:tblPr><w:tblW w:w="5000" w:type="pct"/>${borders}<w:tblLayout w:type="autofit"/></w:tblPr>
    <w:tblGrid><w:gridCol w:w="3500"/><w:gridCol w:w="2000"/><w:gridCol w:w="2000"/></w:tblGrid>
    ${parts.join("")}
  </w:tbl>`;
}

export function buildSalaryStructureHtmlTable(rows = EMPTY_SALARY_ROWS) {
  const applicable = filterApplicableSalaryRows(rows);
  if (!applicable.length) {
    return `<p style="margin:12px 0;padding:10px 12px;border:1px dashed #9ca3af;color:#4b5563;font-size:11pt;font-family:Segoe UI,Arial,sans-serif;">
      No salary components / employee structure available. Configure salary components and assign a structure to the employee before generating letters with a CTC table.
    </p>`;
  }
  const s = partitionSalaryRows(applicable);
  const fmt = (v) => (v == null || v === "" ? "—" : formatInr(v));
  const sectionHtml = (title, list, totalLabel, totalMo, totalAn) => {
    if (!list.length) return "";
    const body = list
      .map(
        (r) =>
          `<tr>
            <td style="padding:6px 8px;border:1px solid #ccc;">${r.name}</td>
            <td style="padding:6px 8px;border:1px solid #ccc;text-align:right;">${fmt(r.monthly)}</td>
            <td style="padding:6px 8px;border:1px solid #ccc;text-align:right;">${fmt(r.annual)}</td>
          </tr>`
      )
      .join("");
    return `<tr style="background:#e5e7eb;"><td colspan="3" style="padding:6px 8px;border:1px solid #ccc;font-weight:700;">${title}</td></tr>
      ${body}
      <tr>
        <td style="padding:6px 8px;border:1px solid #ccc;font-weight:600;">${totalLabel}</td>
        <td style="padding:6px 8px;border:1px solid #ccc;text-align:right;font-weight:600;">${fmt(totalMo)}</td>
        <td style="padding:6px 8px;border:1px solid #ccc;text-align:right;font-weight:600;">${fmt(totalAn)}</td>
      </tr>`;
  };
  return `<table style="width:100%;border-collapse:collapse;font-size:inherit;font-family:inherit;margin:12px 0;">
    <thead>
      <tr style="background:#f3f4f6;">
        <th style="padding:6px 8px;border:1px solid #ccc;text-align:left;">Component</th>
        <th style="padding:6px 8px;border:1px solid #ccc;text-align:right;">Monthly</th>
        <th style="padding:6px 8px;border:1px solid #ccc;text-align:right;">Annual</th>
      </tr>
    </thead>
    <tbody>
      ${sectionHtml("Earnings", s.earnings, "Gross (Earnings)", s.grossMonthly, s.grossAnnual)}
      ${sectionHtml("Benefits", s.benefits, "Total Benefits", s.benefitsMonthly, s.benefitsAnnual)}
      ${sectionHtml("Reimbursements", s.reimbursements, "Total Reimbursements", s.reimbMonthly, s.reimbAnnual)}
      ${sectionHtml("Deductions", s.deductions, "Total Deductions", s.dedMonthly, s.dedAnnual)}
      <tr>
        <td style="padding:6px 8px;border:1px solid #ccc;font-weight:700;">Net Pay</td>
        <td style="padding:6px 8px;border:1px solid #ccc;text-align:right;font-weight:700;">${fmt(s.netMonthly)}</td>
        <td style="padding:6px 8px;border:1px solid #ccc;text-align:right;font-weight:700;">${fmt(s.netAnnual)}</td>
      </tr>
      <tr>
        <td style="padding:6px 8px;border:1px solid #ccc;font-weight:700;">Total CTC</td>
        <td style="padding:6px 8px;border:1px solid #ccc;text-align:right;font-weight:700;">${fmt(s.ctcMonthly)}</td>
        <td style="padding:6px 8px;border:1px solid #ccc;text-align:right;font-weight:700;">${fmt(s.ctcAnnual)}</td>
      </tr>
    </tbody>
  </table>`;
}

function findRowAmount(rows, codes) {
  const list = Array.isArray(codes) ? codes : [codes];
  for (const code of list) {
    const want = String(code).toUpperCase();
    const row = (rows || []).find((r) => {
      const rc = String(r.code || "").toUpperCase();
      const rn = String(r.name || "")
        .toUpperCase()
        .replace(/\s+/g, "_");
      const rnSpaced = String(r.name || "").toUpperCase();
      if (rc === want || rn === want) return true;
      // fuzzy: BASIC matches "Basic", "Basic Salary"
      const token = want.replace(/_/g, " ");
      if (rnSpaced === token || rnSpaced.startsWith(`${token} `) || rnSpaced.includes(` ${token}`)) {
        return true;
      }
      if (want === "HRA" && /HOUSE\s*RENT|HRA/.test(rnSpaced)) return true;
      if (want === "BASIC" && /\bBASIC\b/.test(rnSpaced)) return true;
      return false;
    });
    if (row && row.monthly != null) return String(row.monthly);
  }
  return "";
}

export function buildSalaryFieldMapFromRows(rows = []) {
  const applicable = filterApplicableSalaryRows(rows);
  const parts = partitionSalaryRows(applicable);
  const has = applicable.length > 0;
  const fmt = (v) => (v == null || v === "" ? "" : formatInr(v));
  const map = {
    basic_salary: findRowAmount(applicable, ["BASIC", "BASIC_SALARY"]),
    hra: findRowAmount(applicable, ["HRA", "HOUSE_RENT_ALLOWANCE"]),
    special_allowance: findRowAmount(applicable, ["SPECIAL", "SPECIAL_ALLOWANCE"]),
    conveyance_allowance: findRowAmount(applicable, ["CONV", "CONVEYANCE", "CONVEYANCE_ALLOWANCE"]),
    medical_allowance: findRowAmount(applicable, ["MED", "MEDICAL", "MEDICAL_ALLOWANCE"]),
    employer_pf: findRowAmount(applicable, ["EPF_ER", "EMPLOYER_PF", "PF_EMPLOYER"]),
    nps_employer: findRowAmount(applicable, ["NPS_EMPLOYER", "EMPLOYER_NPS", "NPS (EMPLOYER)"]),
    vpf: findRowAmount(applicable, ["VPF", "EMPLOYEE_VPF"]),
    meal_voucher: findRowAmount(applicable, ["MEAL_VOUCHER", "MEAL VOUCHER", "FOOD COUPON"]),
    gratuity: findRowAmount(applicable, ["GRAT", "GRATUITY"]),
    gross_salary: has ? fmt(parts.grossMonthly) : "",
    net_salary: has ? fmt(parts.netMonthly) : "",
    total_ctc: has ? fmt(parts.ctcAnnual) : "",
    total_ctc_monthly: has ? fmt(parts.ctcMonthly) : "",
    salary_structure_table: has ? buildSalaryStructureHtmlTable(rows) : "",
    salary_structure_text: has ? buildSalaryStructurePlainText(rows) : "",
  };
  applicable.forEach((r) => {
    if (!r.code) return;
    map[`comp_${r.code}`] = r.monthly == null ? "" : fmt(r.monthly);
  });
  ["basic_salary", "hra", "special_allowance", "conveyance_allowance", "medical_allowance", "employer_pf", "nps_employer", "vpf", "meal_voucher", "gratuity"].forEach(
    (k) => {
      if (map[k] !== "" && map[k] != null && !Number.isNaN(Number(String(map[k]).replace(/,/g, "")))) {
        map[k] = fmt(Number(String(map[k]).replace(/,/g, "")));
      }
    }
  );
  return map;
}

/** Build salary tags from company component library (for Available Tags UI). */
export function buildDynamicSalaryTags(components = []) {
  return (components || [])
    .filter((c) => c && c.isActive !== false && c.code)
    .map((c) => ({
      tag: `{{comp_${String(c.code).toUpperCase()}}}`,
      description: `${c.name || c.code} (monthly, from employee structure)`,
    }));
}

export const LETTER_TAG_CATALOG = {
  employee: [
    { tag: "{{employee_name}}", description: "Full name of the employee" },
    { tag: "{{employee_id}}", description: "Employee ID" },
    { tag: "{{designation}}", description: "Current designation" },
    { tag: "{{department}}", description: "Department name" },
    { tag: "{{reporting_manager}}", description: "Reporting manager name" },
    { tag: "{{work_location}}", description: "Work location" },
    { tag: "{{joining_date}}", description: "Date of joining" },
    { tag: "{{leaving_date}}", description: "Date of leaving" },
    { tag: "{{current_department}}", description: "Current department" },
    { tag: "{{new_department}}", description: "New department" },
    { tag: "{{current_designation}}", description: "Current designation" },
    { tag: "{{new_designation}}", description: "New designation" },
    { tag: "{{current_location}}", description: "Current work location" },
    { tag: "{{new_location}}", description: "New work location" },
    { tag: "{{new_reporting_manager}}", description: "New reporting manager" },
  ],
  salary: [
    { tag: "{{basic_salary}}", description: "Basic salary (monthly) — from employee structure if BASIC exists" },
    { tag: "{{hra}}", description: "HRA (monthly) — from employee structure if HRA exists" },
    { tag: "{{special_allowance}}", description: "Special allowance (monthly)" },
    { tag: "{{conveyance_allowance}}", description: "Conveyance allowance (monthly)" },
    { tag: "{{medical_allowance}}", description: "Medical allowance (monthly)" },
    { tag: "{{employer_pf}}", description: "Employer PF (monthly)" },
    { tag: "{{nps_employer}}", description: "Employer NPS benefit (monthly)" },
    { tag: "{{vpf}}", description: "VPF employee deduction (monthly)" },
    { tag: "{{meal_voucher}}", description: "Meal voucher (monthly)" },
    { tag: "{{gratuity}}", description: "Gratuity (monthly)" },
    { tag: "{{gross_salary}}", description: "Gross earnings (monthly)" },
    { tag: "{{net_salary}}", description: "Net salary after deductions (monthly)" },
    { tag: "{{total_ctc}}", description: "Total CTC (annual) from employee structure" },
    { tag: "{{total_ctc_monthly}}", description: "Total CTC (monthly)" },
    {
      tag: "{{salary_structure_table}}",
      description: "Full CTC table from the selected employee’s salary structure (HTML in preview)",
    },
    {
      tag: "{{salary_structure_text}}",
      description: "Salary structure as plain text (best for Word .docx)",
    },
  ],
  company: [
    { tag: "{{company_name}}", description: "Company name" },
    { tag: "{{company_address}}", description: "Company address" },
    { tag: "{{hr_manager_name}}", description: "HR Manager name" },
    { tag: "{{ceo_name}}", description: "CEO name" },
  ],
  dates: [
    { tag: "{{appointment_date}}", description: "Appointment date" },
    { tag: "{{confirmation_date}}", description: "Confirmation date" },
    { tag: "{{transfer_date}}", description: "Transfer date" },
    { tag: "{{resignation_date}}", description: "Resignation date" },
    { tag: "{{last_working_date}}", description: "Last working date" },
    { tag: "{{probation_period}}", description: "Probation period" },
    { tag: "{{notice_period}}", description: "Notice period" },
    { tag: "{{working_hours}}", description: "Working hours" },
  ],
  other: [
    { tag: "{{leaving_reason}}", description: "Reason for leaving" },
    { tag: "{{resignation_reason}}", description: "Resignation reason" },
  ],
  promotion: [
    { tag: "{{new_designation}}", description: "New designation after promotion" },
    { tag: "{{current_designation}}", description: "Current designation before promotion" },
    { tag: "{{promotion_date}}", description: "Date of promotion" },
    { tag: "{{new_reporting_manager}}", description: "New reporting manager after promotion" },
  ],
  salaryRevision: [
    { tag: "{{increment_date}}", description: "Date of salary increment" },
    { tag: "{{previous_basic_salary}}", description: "Previous basic salary amount" },
    { tag: "{{new_basic_salary}}", description: "New basic salary amount" },
    { tag: "{{previous_total_ctc}}", description: "Previous total CTC" },
    { tag: "{{new_total_ctc}}", description: "New total CTC" },
    { tag: "{{increment_percentage}}", description: "Increment percentage" },
  ],
  probation: [
    { tag: "{{extension_period}}", description: "Probation extension period in months" },
    { tag: "{{extension_date}}", description: "Date of probation extension" },
    { tag: "{{original_probation_period}}", description: "Original probation period" },
    { tag: "{{new_probation_end_date}}", description: "New probation end date" },
    { tag: "{{extension_reason}}", description: "Reason for probation extension" },
  ],
  termination: [
    { tag: "{{termination_date}}", description: "Date of termination" },
    { tag: "{{termination_reason}}", description: "Reason for termination" },
  ],
  contract: [
    { tag: "{{contract_start_date}}", description: "Contract start date" },
    { tag: "{{contract_end_date}}", description: "Contract end date" },
    { tag: "{{contract_duration}}", description: "Contract duration in months" },
    { tag: "{{previous_contract_end_date}}", description: "Previous contract end date" },
    { tag: "{{new_contract_start_date}}", description: "New contract start date" },
    { tag: "{{renewal_duration}}", description: "Contract renewal duration" },
  ],
};

/**
 * Empty merge shell for template authoring. Does NOT invent salary amounts.
 */
export function getHypotheticalEmployee(company = {}, { salaryRows = [] } = {}) {
  const salaryMap = buildSalaryFieldMapFromRows(salaryRows);
  return {
    name: "",
    employeeId: "",
    designation: "",
    department: "",
    reportingManager: "",
    workLocation: "",
    ...salaryMap,
    company_name: company.name || "",
    company_address: company.address || "",
    hr_manager_name: "",
    ceo_name: "",
    joining_date: "",
    leaving_date: "",
    appointment_date: "",
    confirmation_date: "",
    transfer_date: "",
    resignation_date: "",
    last_working_date: "",
    probation_period: "",
    notice_period: "",
    working_hours: "",
    leaving_reason: "",
    resignation_reason: "",
    current_department: "",
    new_department: "",
    current_designation: "",
    new_designation: "",
    current_location: "",
    new_location: "",
    new_reporting_manager: "",
    promotion_date: "",
    increment_date: "",
    previous_basic_salary: "",
    new_basic_salary: "",
    previous_total_ctc: "",
    new_total_ctc: "",
    increment_percentage: "",
    extension_period: "",
    extension_date: "",
    original_probation_period: "",
    new_probation_end_date: "",
    extension_reason: "",
    termination_date: "",
    termination_reason: "",
    contract_start_date: "",
    contract_end_date: "",
    contract_duration: "",
    previous_contract_end_date: "",
    new_contract_start_date: "",
    renewal_duration: "",
  };
}

function formatPersonName(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  if (typeof value !== "object") return "";
  const user = value.user || value.User || null;
  const parts = [
    user?.name || value.name || value.firstName || value.fullName || "",
    user?.lastName || value.lastName || "",
  ]
    .map((p) => String(p || "").trim())
    .filter(Boolean);
  if (parts.length) return parts.join(" ");
  return String(value.empCode || value.employeeId || value.email || "").trim();
}

function formatMasterName(value, fallback = "") {
  if (value == null || value === "") return fallback || "";
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  if (typeof value === "object") {
    return String(value.name || value.label || value.title || fallback || "").trim();
  }
  return fallback || "";
}

/** Resolve reporting manager display name from nested objects / alternate keys. */
export function resolveReportingManagerName(employee = {}) {
  const direct =
    formatPersonName(employee.reportingManager) ||
    formatPersonName(employee.reporting_manager) ||
    formatPersonName(employee.manager) ||
    String(employee.reportingManagerName || employee.reporting_manager_name || "").trim();
  if (direct && direct !== "[object Object]") return direct;
  return "";
}

/** Merge employee + company + real salary rows for letter generation. */
export function buildLetterMergeContext({
  company = {},
  employee = {},
  salaryRows = [],
  structure = null,
} = {}) {
  let salaryMap = buildSalaryFieldMapFromRows(salaryRows);
  salaryMap = applyStructureSummaryToSalaryMap(salaryMap, structure);
  salaryMap = applyEmployeeProfileCtcFallback(salaryMap, employee);

  // Net from summary when available
  const summary =
    structure?.computedSummaryJson && typeof structure.computedSummaryJson === "object"
      ? structure.computedSummaryJson
      : {};
  if ((!salaryMap.net_salary || salaryMap.net_salary === "") && summary.net != null) {
    salaryMap.net_salary = formatInr(Math.round(Number(summary.net)));
  }

  const joining =
    formatDateForLetter(
      employee.joining_date ||
        employee.joiningDate ||
        employee.dateOfJoining ||
        employee.date_of_joining ||
        employee.doj
    ) || "";
  const today = formatDateForLetter(new Date());

  const reportingManagerName = resolveReportingManagerName(employee);
  const designation = formatMasterName(employee.designation, employee.designationName || "");
  const department = formatMasterName(employee.department, employee.departmentName || "");
  const workLocation = formatMasterName(
    employee.workLocation || employee.branch || employee.work_location,
    employee.branchName || employee.location || ""
  );

  const base = getHypotheticalEmployee(company, { salaryRows: [] });
  // Avoid spreading nested objects that overwrite string tag fields
  const {
    reportingManager: _rm,
    reporting_manager: _rm2,
    designation: _des,
    department: _dep,
    branch: _br,
    user: _user,
    ...employeeRest
  } = employee || {};

  return {
    ...base,
    ...employeeRest,
    name:
      employee.name ||
      employee.fullName ||
      formatPersonName(employee.user) ||
      "",
    employeeId: employee.employeeId || employee.empCode || "",
    designation,
    department,
    reportingManager: reportingManagerName,
    reporting_manager: reportingManagerName,
    workLocation,
    work_location: workLocation,
    company_name: company.name || employee.company_name || "",
    company_address: company.address || employee.company_address || "",
    hr_manager_name:
      employee.hr_manager_name || company.hrManagerName || company.hr_manager_name || "HR Manager",
    joining_date: joining,
    appointment_date:
      formatDateForLetter(employee.appointment_date || employee.appointmentDate) || joining || today,
    confirmation_date:
      formatDateForLetter(employee.confirmation_date || employee.confirmationDate) || "",
    transfer_date: formatDateForLetter(employee.transfer_date || employee.transferDate) || today,
    resignation_date:
      formatDateForLetter(employee.resignation_date || employee.resignationDate) || today,
    last_working_date:
      formatDateForLetter(employee.last_working_date || employee.lastWorkingDate) || "",
    leaving_date: formatDateForLetter(employee.leaving_date || employee.leavingDate) || "",
    probation_period:
      employee.probation_period ||
      employee.probationPeriod ||
      company.probationPeriod ||
      "3",
    notice_period:
      employee.notice_period || employee.noticePeriod || company.noticePeriod || "30",
    working_hours:
      employee.working_hours || employee.workingHours || company.workingHours || "09:00 hours to 18:00 hours",
    current_department: employee.current_department || department,
    current_designation: employee.current_designation || designation,
    current_location: employee.current_location || workLocation,
    new_department: employee.new_department || "",
    new_designation: employee.new_designation || "",
    new_location: employee.new_location || "",
    new_reporting_manager:
      formatPersonName(employee.new_reporting_manager) || reportingManagerName,
    ...salaryMap,
  };
}

const TAG_VALUE_RESOLVERS = {
  employee_name: (e) => e.name,
  employee_id: (e) => e.employeeId,
  designation: (e) => e.designation,
  department: (e) => e.department,
  reporting_manager: (e) => e.reportingManager,
  work_location: (e) => e.workLocation,
  joining_date: (e) => e.joining_date,
  leaving_date: (e) => e.leaving_date,
  current_department: (e) => e.current_department,
  new_department: (e) => e.new_department,
  current_designation: (e) => e.current_designation,
  new_designation: (e) => e.new_designation,
  current_location: (e) => e.current_location,
  new_location: (e) => e.new_location,
  new_reporting_manager: (e) => e.new_reporting_manager,
  basic_salary: (e) => e.basic_salary,
  hra: (e) => e.hra,
  special_allowance: (e) => e.special_allowance,
  conveyance_allowance: (e) => e.conveyance_allowance,
  medical_allowance: (e) => e.medical_allowance,
  employer_pf: (e) => e.employer_pf,
  nps_employer: (e) => e.nps_employer,
  vpf: (e) => e.vpf,
  meal_voucher: (e) => e.meal_voucher,
  gratuity: (e) => e.gratuity,
  gross_salary: (e) => e.gross_salary,
  net_salary: (e) => e.net_salary,
  total_ctc: (e) => e.total_ctc,
  total_ctc_monthly: (e) => e.total_ctc_monthly,
  salary_structure_table: (e) => e.salary_structure_table,
  salary_structure_text: (e) => e.salary_structure_text,
  company_name: (e) => e.company_name,
  company_address: (e) => e.company_address,
  hr_manager_name: (e) => e.hr_manager_name,
  ceo_name: (e) => e.ceo_name,
  appointment_date: (e) => e.appointment_date,
  confirmation_date: (e) => e.confirmation_date,
  transfer_date: (e) => e.transfer_date,
  resignation_date: (e) => e.resignation_date,
  last_working_date: (e) => e.last_working_date,
  probation_period: (e) => e.probation_period,
  notice_period: (e) => e.notice_period,
  working_hours: (e) => e.working_hours,
  leaving_reason: (e) => e.leaving_reason,
  resignation_reason: (e) => e.resignation_reason,
  promotion_date: (e) => e.promotion_date,
  increment_date: (e) => e.increment_date,
  previous_basic_salary: (e) => e.previous_basic_salary,
  new_basic_salary: (e) => e.new_basic_salary,
  previous_total_ctc: (e) => e.previous_total_ctc,
  new_total_ctc: (e) => e.new_total_ctc,
  increment_percentage: (e) => e.increment_percentage,
  extension_period: (e) => e.extension_period,
  extension_date: (e) => e.extension_date,
  original_probation_period: (e) => e.original_probation_period,
  new_probation_end_date: (e) => e.new_probation_end_date,
  extension_reason: (e) => e.extension_reason,
  termination_date: (e) => e.termination_date,
  termination_reason: (e) => e.termination_reason,
  contract_start_date: (e) => e.contract_start_date,
  contract_end_date: (e) => e.contract_end_date,
  contract_duration: (e) => e.contract_duration,
  previous_contract_end_date: (e) => e.previous_contract_end_date,
  new_contract_start_date: (e) => e.new_contract_start_date,
  renewal_duration: (e) => e.renewal_duration,
};

export function resolveTagValue(tagName, employee) {
  const resolver = TAG_VALUE_RESOLVERS[tagName];
  if (resolver) {
    const v = resolver(employee);
    if (v != null && v !== "") return String(v);
  }
  if (employee && employee[tagName] != null && employee[tagName] !== "") {
    return String(employee[tagName]);
  }
  return "";
}

export function replaceLetterTags(
  content,
  employee,
  { preferPlainSalaryTable = false, keepTags = false } = {}
) {
  if (content == null) return "";
  if (keepTags) return String(content);
  let generated = String(content);
  const emp = { ...employee };
  if (preferPlainSalaryTable) {
    emp.salary_structure_table =
      emp.salary_structure_text ||
      (emp.salary_structure_table && !String(emp.salary_structure_table).includes("<table")
        ? emp.salary_structure_table
        : "");
  }

  Object.values(LETTER_TAG_CATALOG)
    .flat()
    .forEach(({ tag }) => {
      const tagName = tag.replace(/[{}]/g, "");
      const value = resolveTagValue(tagName, emp);
      const regex = new RegExp(tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
      generated = generated.replace(regex, value);
    });

  generated = generated.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, name) =>
    resolveTagValue(name, emp)
  );

  return generated;
}

export function getLetterTagValueMap(employee, options = {}) {
  const map = {};
  Object.values(LETTER_TAG_CATALOG)
    .flat()
    .forEach(({ tag }) => {
      map[tag] = replaceLetterTags(tag, employee, options);
    });
  Object.keys(employee || {}).forEach((key) => {
    if (key.startsWith("comp_")) {
      map[`{{${key}}}`] = employee[key] != null ? String(employee[key]) : "";
    }
  });

  const rows = Array.isArray(options.salaryRows) ? options.salaryRows : null;
  if (options.forDocx) {
    // Real Word table grid (Component | Monthly | Annual)
    map["{{salary_structure_table}}"] = buildSalaryStructureWordTableXml(
      rows || []
    );
    map["{{salary_structure_text}}"] =
      employee?.salary_structure_text || buildSalaryStructurePlainText(rows || []);
  } else if (options?.preferPlainSalaryTable !== false) {
    map["{{salary_structure_table}}"] =
      employee?.salary_structure_text ||
      (employee?.salary_structure_table &&
      !String(employee.salary_structure_table).includes("<table")
        ? employee.salary_structure_table
        : buildSalaryStructurePlainText(rows || []));
  }
  return map;
}

function decodeXmlEntities(input = "") {
  return input
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'");
}

function escapeXml(input = "") {
  return String(input)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/** Convert plain newlines into Word break runs so annexure tables show as multiple lines. */
function textToWordRunXml(text, openTag = '<w:t xml:space="preserve">') {
  const parts = String(text ?? "").split(/\r\n|\n|\r/);
  return parts
    .map((part, idx) => {
      const body = `${openTag}${escapeXml(part)}</w:t>`;
      if (idx === 0) return `<w:r>${body}</w:r>`;
      return `<w:r><w:br/></w:r><w:r>${body}</w:r>`;
    })
    .join("");
}

/**
 * Replace {{tags}} inside DOCX XML even when Word splits a placeholder across multiple <w:t> runs.
 * If a tag value is a Word table (`<w:tbl...>`), the whole paragraph is replaced by that table
 * (tables cannot live inside <w:t>).
 */
export function replaceTagsInDocxXml(xml, tagValueMap) {
  if (!xml) return xml;

  const tableTagKeys = Object.entries(tagValueMap || {})
    .filter(([, val]) => typeof val === "string" && val.trim().startsWith("<w:tbl"))
    .map(([tag]) => tag);

  const applyJoined = (joined, { skipTableTags = false } = {}) => {
    let out = joined;
    let changed = false;
    Object.entries(tagValueMap || {}).forEach(([tag, val]) => {
      if (!tag || !out.includes(tag)) return;
      if (skipTableTags && typeof val === "string" && val.trim().startsWith("<w:tbl")) return;
      out = out.split(tag).join(String(val ?? ""));
      changed = true;
    });
    out = out.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (full, name) => {
      const key = `{{${name}}}`;
      const val = tagValueMap?.[key];
      if (val == null) return full;
      if (skipTableTags && typeof val === "string" && val.trim().startsWith("<w:tbl")) return full;
      changed = true;
      return String(val);
    });
    return { out, changed };
  };

  return xml.replace(/<w:p\b[\s\S]*?<\/w:p>/g, (paragraphXml) => {
    const textNodeRegex = /(<w:t\b[^>]*>)([\s\S]*?)(<\/w:t>)/g;
    const matches = [];
    let m;
    while ((m = textNodeRegex.exec(paragraphXml)) !== null) {
      matches.push({
        full: m[0],
        open: m[1],
        text: decodeXmlEntities(m[2] || ""),
        close: m[3],
        index: m.index,
        length: m[0].length,
      });
    }
    if (!matches.length) return paragraphXml;

    const joined = matches.map((x) => x.text).join("");

    // Paragraph that is (only) a salary-table tag → inject real Word table
    for (const tag of tableTagKeys) {
      const tagName = tag.replace(/[{}]/g, "");
      const onlyTag = new RegExp(`^\\s*\\{\\{\\s*${tagName}\\s*\\}\\}\\s*$`);
      if (onlyTag.test(joined)) {
        return String(tagValueMap[tag]).trim();
      }
      if (joined.includes(tag) || new RegExp(`\\{\\{\\s*${tagName}\\s*\\}\\}`).test(joined)) {
        const { out: textOut } = applyJoined(joined, { skipTableTags: true });
        const remainder = textOut
          .replace(new RegExp(`\\{\\{\\s*${tagName}\\s*\\}\\}`, "g"), "")
          .trim();
        const openP = paragraphXml.match(/^<w:p\b[^>]*>/)?.[0] || "<w:p>";
        const pPrMatch = paragraphXml.match(/<w:pPr\b[\s\S]*?<\/w:pPr>/);
        const pPr = pPrMatch ? pPrMatch[0] : "";
        const prefix = remainder
          ? `${openP}${pPr}${textToWordRunXml(remainder)}</w:p>`
          : "";
        return `${prefix}${String(tagValueMap[tag]).trim()}`;
      }
    }

    const { out, changed } = applyJoined(joined, { skipTableTags: true });
    if (!changed) return paragraphXml;

    const hasBreaks = /[\r\n]/.test(out);
    let result = paragraphXml;

    if (hasBreaks) {
      const pPrMatch = paragraphXml.match(/<w:pPr\b[\s\S]*?<\/w:pPr>/);
      const pPr = pPrMatch ? pPrMatch[0] : "";
      const openP = paragraphXml.match(/^<w:p\b[^>]*>/)?.[0] || "<w:p>";
      return `${openP}${pPr}${textToWordRunXml(out)}</w:p>`;
    }

    const escaped = escapeXml(out);
    for (let i = matches.length - 1; i >= 0; i -= 1) {
      const match = matches[i];
      const newText = i === 0 ? escaped : "";
      let open = match.open;
      if (
        newText &&
        (/\s/.test(newText[0] || "") || /\s/.test(newText[newText.length - 1] || "")) &&
        !/xml:space=/.test(open)
      ) {
        open = open.replace("<w:t", '<w:t xml:space="preserve"');
      }
      const replacement = `${open}${newText}${match.close}`;
      result =
        result.slice(0, match.index) +
        replacement +
        result.slice(match.index + match.length);
    }
    return result;
  });
}

/** Apply tag map to a .docx ArrayBuffer and return a new ArrayBuffer ready for preview/download. */
export async function applyTagsToDocxBuffer(arrayBuffer, tagValueMap) {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const targets = Object.keys(zip.files).filter((k) =>
    /^word\/(document|header\d*|footer\d*)\.xml$/i.test(k)
  );

  await Promise.all(
    targets.map(async (path) => {
      const file = zip.file(path);
      if (!file) return;
      const xml = await file.async("text");
      zip.file(path, replaceTagsInDocxXml(xml, tagValueMap));
    })
  );

  return zip.generateAsync({ type: "arraybuffer" });
}

export async function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}
